import { useParams, useNavigate } from 'react-router-dom';
import { useProspect } from '../hooks/useProspects';
import { ScoreBadge } from '../components/ScoreBadge';
import { StateBadge } from '../components/StateBadge';
import { EvidenceCard } from '../components/EvidenceCard';
import type { ProspectScores, StateEvent } from '../types';

function ScoreBar({ label, score }: { label: string; score: number }) {
  const color = score >= 71 ? '#10b981' : score >= 41 ? '#f59e0b' : '#ef4444';
  return (
    <div style={{ marginBottom: '12px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
        <span style={{ fontSize: '13px', fontWeight: 500, color: '#374151' }}>{label}</span>
        <ScoreBadge score={score} size="sm" />
      </div>
      <div style={{ height: '6px', backgroundColor: '#f3f4f6', borderRadius: '3px', overflow: 'hidden' }}>
        <div
          style={{
            height: '100%',
            width: `${score}%`,
            backgroundColor: color,
            borderRadius: '3px',
            transition: 'width 0.5s ease',
          }}
        />
      </div>
    </div>
  );
}

function ScoresPanel({ scores }: { scores: ProspectScores }) {
  return (
    <div
      style={{
        backgroundColor: '#fff',
        border: '1px solid #e5e7eb',
        borderRadius: '10px',
        padding: '20px 22px',
      }}
    >
      <h3 style={{ margin: '0 0 16px', fontSize: '15px', fontWeight: 700, color: '#374151' }}>
        Qualification Scores
      </h3>
      <ScoreBar label="ICP Fit" score={scores.icp} />
      <ScoreBar label="Pain Signal" score={scores.pain} />
      <ScoreBar label="Timing" score={scores.timing} />
      <ScoreBar label="Evidence Quality" score={scores.evidence} />
      <ScoreBar label="Intent" score={scores.intent} />
      <div style={{ borderTop: '1px solid #f3f4f6', paddingTop: '12px', marginTop: '4px' }}>
        <ScoreBar label="Overall" score={scores.overall} />
      </div>
    </div>
  );
}

function StateTimeline({ history }: { history: StateEvent[] }) {
  return (
    <div
      style={{
        backgroundColor: '#fff',
        border: '1px solid #e5e7eb',
        borderRadius: '10px',
        padding: '20px 22px',
      }}
    >
      <h3 style={{ margin: '0 0 16px', fontSize: '15px', fontWeight: 700, color: '#374151' }}>
        State Timeline
      </h3>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0' }}>
        {[...history].reverse().map((evt, i) => (
          <div key={i} style={{ display: 'flex', gap: '14px', paddingBottom: '14px', position: 'relative' }}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <div
                style={{
                  width: '10px',
                  height: '10px',
                  borderRadius: '50%',
                  backgroundColor: i === 0 ? '#2563eb' : '#d1d5db',
                  marginTop: '3px',
                  flexShrink: 0,
                }}
              />
              {i < history.length - 1 && (
                <div style={{ width: '2px', flex: 1, backgroundColor: '#f3f4f6', marginTop: '4px' }} />
              )}
            </div>
            <div style={{ flex: 1 }}>
              <StateBadge state={evt.state} />
              {evt.notes && (
                <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>{evt.notes}</div>
              )}
              <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '2px' }}>
                {new Date(evt.timestamp).toLocaleString()}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const CARD = {
  backgroundColor: '#fff',
  border: '1px solid #e5e7eb',
  borderRadius: '10px',
  padding: '20px 22px',
};

export function ProspectDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: prospect, isLoading, isError } = useProspect(id ?? null);

  if (isLoading) {
    return (
      <div style={{ padding: '60px', textAlign: 'center', color: '#9ca3af' }}>Loading prospect…</div>
    );
  }

  if (isError || !prospect) {
    return (
      <div style={{ padding: '60px', textAlign: 'center', color: '#991b1b' }}>
        Failed to load prospect.
        <br />
        <button onClick={() => navigate('/prospects')} style={{ marginTop: '12px', cursor: 'pointer' }}>
          ← Back to Prospects
        </button>
      </div>
    );
  }

  return (
    <div style={{ padding: '32px 36px', maxWidth: '1100px' }}>
      {/* Header */}
      <div style={{ marginBottom: '24px' }}>
        <button
          onClick={() => navigate('/prospects')}
          style={{
            background: 'none',
            border: 'none',
            color: '#6b7280',
            cursor: 'pointer',
            fontSize: '13px',
            padding: '0 0 10px',
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
          }}
        >
          ← Prospects
        </button>
        <div style={{ display: 'flex', alignItems: 'center', gap: '14px', flexWrap: 'wrap' }}>
          <h1 style={{ margin: 0, fontSize: '26px', fontWeight: 800, color: '#111827' }}>
            {prospect.companyName}
          </h1>
          <StateBadge state={prospect.state} />
        </div>
        <a
          href={`https://${prospect.domain}`}
          target="_blank"
          rel="noopener noreferrer"
          style={{ fontSize: '14px', color: '#2563eb', textDecoration: 'none' }}
        >
          {prospect.domain} ↗
        </a>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 340px', gap: '20px' }}>
        {/* Left column */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          {/* Company Info */}
          <div style={CARD}>
            <h3 style={{ margin: '0 0 14px', fontSize: '15px', fontWeight: 700, color: '#374151' }}>
              Company Info
            </h3>
            <dl style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', margin: 0 }}>
              {[
                ['Industry', prospect.industry],
                ['Employees', prospect.employees],
                ['HQ', prospect.hq],
                ['Domain', prospect.domain],
              ].map(([label, value]) =>
                value ? (
                  <div key={label}>
                    <dt style={{ fontSize: '11px', fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase' }}>{label}</dt>
                    <dd style={{ margin: '2px 0 0', fontSize: '14px', color: '#111827' }}>{value}</dd>
                  </div>
                ) : null,
              )}
            </dl>
          </div>

          {/* Decision Maker */}
          {prospect.decisionMaker && (
            <div style={CARD}>
              <h3 style={{ margin: '0 0 14px', fontSize: '15px', fontWeight: 700, color: '#374151' }}>
                Decision Maker
              </h3>
              <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                <div
                  style={{
                    width: '44px',
                    height: '44px',
                    borderRadius: '50%',
                    backgroundColor: '#e0e7ff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '18px',
                    fontWeight: 700,
                    color: '#3730a3',
                    flexShrink: 0,
                  }}
                >
                  {prospect.decisionMaker.name[0]}
                </div>
                <div>
                  <div style={{ fontSize: '15px', fontWeight: 700, color: '#111827' }}>
                    {prospect.decisionMaker.name}
                  </div>
                  <div style={{ fontSize: '13px', color: '#6b7280' }}>{prospect.decisionMaker.title}</div>
                  {prospect.decisionMaker.linkedinUrl && (
                    <a
                      href={prospect.decisionMaker.linkedinUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ fontSize: '12px', color: '#2563eb', textDecoration: 'none' }}
                    >
                      LinkedIn ↗
                    </a>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Contact */}
          {prospect.contact && (
            <div style={CARD}>
              <h3 style={{ margin: '0 0 14px', fontSize: '15px', fontWeight: 700, color: '#374151' }}>
                Contact
              </h3>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <span style={{ fontSize: '15px', color: '#111827', fontFamily: 'monospace' }}>
                  {prospect.contact.email}
                </span>
                <span
                  style={{
                    fontSize: '12px',
                    fontWeight: 600,
                    padding: '2px 8px',
                    borderRadius: '4px',
                    backgroundColor: prospect.contact.verified ? '#d1fae5' : '#fef3c7',
                    color: prospect.contact.verified ? '#065f46' : '#92400e',
                  }}
                >
                  {prospect.contact.verified ? '✓ Verified' : '~ Unverified'}
                </span>
                <span style={{ fontSize: '12px', color: '#6b7280' }}>
                  {Math.round(prospect.contact.confidence * 100)}% confidence
                </span>
              </div>
              {prospect.contact.verificationMethod && (
                <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '6px' }}>
                  Method: {prospect.contact.verificationMethod}
                </div>
              )}
            </div>
          )}

          {/* Audit Hypothesis */}
          {prospect.auditHypothesis && (
            <div style={CARD}>
              <h3 style={{ margin: '0 0 12px', fontSize: '15px', fontWeight: 700, color: '#374151' }}>
                Audit Hypothesis
              </h3>
              <p style={{ margin: 0, fontSize: '14px', color: '#374151', lineHeight: 1.7, whiteSpace: 'pre-line' }}>
                {prospect.auditHypothesis}
              </p>
            </div>
          )}

          {/* Evidence */}
          {prospect.evidence.length > 0 && (
            <div style={CARD}>
              <h3 style={{ margin: '0 0 14px', fontSize: '15px', fontWeight: 700, color: '#374151' }}>
                Evidence ({prospect.evidence.length})
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {prospect.evidence.map((ev, i) => (
                  <EvidenceCard key={i} evidence={ev} index={i} />
                ))}
              </div>
            </div>
          )}

          {/* Outreach Draft */}
          {prospect.outreachDraft && (
            <div style={{ ...CARD, borderLeft: '4px solid #3b82f6' }}>
              <h3 style={{ margin: '0 0 12px', fontSize: '15px', fontWeight: 700, color: '#374151' }}>
                Outreach Draft
              </h3>
              <div style={{ marginBottom: '10px' }}>
                <div style={{ fontSize: '11px', fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', marginBottom: '4px' }}>Subject</div>
                <div style={{ fontSize: '14px', fontWeight: 600, color: '#111827' }}>{prospect.outreachDraft.subject}</div>
              </div>
              <div>
                <div style={{ fontSize: '11px', fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', marginBottom: '4px' }}>Body</div>
                <pre
                  style={{
                    margin: 0,
                    fontSize: '13px',
                    color: '#374151',
                    lineHeight: 1.7,
                    whiteSpace: 'pre-wrap',
                    fontFamily: 'inherit',
                    backgroundColor: '#f9fafb',
                    padding: '12px',
                    borderRadius: '6px',
                  }}
                >
                  {prospect.outreachDraft.body}
                </pre>
              </div>
            </div>
          )}
        </div>

        {/* Right column */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          <ScoresPanel scores={prospect.scores} />
          <StateTimeline history={prospect.stateHistory} />
        </div>
      </div>
    </div>
  );
}
